"use client";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FC, useState, useEffect, memo } from "react";
import { Button } from "../../shadcn/button";
import { useSwiper } from "swiper/react";
import { cn } from "@/app/utils/cn";
import { swiperButtonVariants } from "@/app/utils/variants/swiperButtonVariants";
import { VariantProps } from "class-variance-authority";
import { Swiper as SwiperType } from "swiper";

interface SwiperButtonProps extends VariantProps<typeof swiperButtonVariants> {
  swiper?: SwiperType | null;
  position: "left" | "right";
  slideMode?: "single" | "group";
}

const SwiperButton: FC<SwiperButtonProps> = ({
  swiper: externalSwiper,
  position,
  slideMode = "single",
  size = "default",
}) => {
  const contextSwiper = useSwiper();
  const swiper = externalSwiper || contextSwiper;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!swiper) return;

    const updateVisibility = () => {
      // Loop modunda KENAR YOKTUR: "ilk"/"son" slide diye bir şey olmadığı için
      // isBeginning/isEnd anlamsız — üstelik Swiper loop sınırını geçerken
      // bunları kararsız raporluyor (son slide'dan başa dönünce sağ buton
      // kayboluyordu). Sonsuz döngüde iki yöne de gidilebildiğine göre iki
      // buton da her zaman açık kalmalı.
      if (swiper.params.loop) {
        setIsVisible(true);
        return;
      }
      setIsVisible(position === "left" ? !swiper.isBeginning : !swiper.isEnd);
    };

    swiper.on("slideChange", updateVisibility);
    swiper.on("reachEnd", updateVisibility);
    swiper.on("reachBeginning", updateVisibility);
    swiper.on("fromEdge", updateVisibility);
    swiper.on("resize", updateVisibility);
    updateVisibility();

    return () => {
      swiper.off("slideChange", updateVisibility);
      swiper.off("reachEnd", updateVisibility);
      swiper.off("reachBeginning", updateVisibility);
      swiper.off("fromEdge", updateVisibility);
      swiper.off("resize", updateVisibility);
    };
  }, [swiper, position]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!swiper) return;

    if (slideMode === "group") {
      const containerWidth = swiper.el.clientWidth;
      const slideWidth = swiper.slides[0]?.clientWidth || 0;
      const spaceBetween = Number(swiper.params.spaceBetween) || 0;
      const slidesPerView = Math.floor(
        containerWidth / (slideWidth + spaceBetween),
      );

      if (position === "left") {
        const newIndex = Math.max(0, swiper.activeIndex - slidesPerView);
        swiper.slideTo(newIndex);
      } else {
        const newIndex = Math.min(
          swiper.slides.length - 1,
          swiper.activeIndex + slidesPerView,
        );
        swiper.slideTo(newIndex);
      }
    } else {
      if (position === "left") {
        swiper.slidePrev();
      } else {
        swiper.slideNext();
      }
    }
  };

  if (!swiper) return null;

  const buttonSize =
    size === "xs"
      ? "icon-xs"
      : size === "sm"
        ? "icon-sm"
        : size === "lg"
          ? "icon-lg"
          : "icon";

  const iconSize =
    size === "xs" ? 12 : size === "sm" ? 16 : size === "lg" ? 24 : 22;

  return (
    <Button
      size={buttonSize}
      className={cn(
        swiperButtonVariants({
          size,
          visibility: isVisible ? "visible" : "hidden",
        }),
      )}
      variant="outline"
      type="button"
      aria-label={position === "left" ? "Önceki" : "Sonraki"}
      onClick={handleClick}
    >
      {position === "left" ? (
        <ChevronLeft size={iconSize} />
      ) : (
        <ChevronRight size={iconSize} />
      )}
    </Button>
  );
};

export default memo(SwiperButton);
